<template>
    <Layout>
        <template v-slot:title>
            <h1>Live Diagnostics</h1>
            <div class="pt-2 text-red text-bold">Records found: {{ totalItems }}</div>
        </template>

        <template v-slot:content>
            <Loader v-if="isLoading"></Loader>

            <div v-if="list.length > 0">
                <div class="card">
                    <div class="table-responsive tbq-table-responsive">
                        <table class="table tbq-table">
                            <thead>
                                <tr>
                                    <th style="width: 20%">Quiz</th>
                                    <th>User ID</th>
                                    <th>Problem</th>
                                    <th>Created At</th>
                                    <th style="width: 10%"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="item in list"
                                    @click="goToDetail(item._id)"
                                    :key="item._id"
                                    class="cursor-pointer"
                                >
                                    <td>
                                        <span v-if="item.quiz">{{ item.quiz.title }}</span>
                                        <span v-else class="text-muted">Unknown</span>
                                    </td>
                                    <td>
                                        <span v-if="item.user">{{ item.user._id }}</span>
                                        <span v-else class="text-muted">Unknown</span>
                                    </td>
                                    <td>
                                        <span
                                            v-if="
                                                item.diagnose_data &&
                                                item.diagnose_data.problems &&
                                                item.diagnose_data.problems.length > 0
                                            "
                                            class="feedback-preview"
                                        >
                                            {{ item.diagnose_data.problems.join(', ') }}
                                        </span>
                                        <span v-else class="text-muted">No problem</span>
                                    </td>
                                    <td>
                                        <div class="d-flex align-items-center">
                                            <ClockIcon size="1.1x" class="mr-2"></ClockIcon>
                                            <span>{{ item.created_at_readable }}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="d-flex">
                                            <FileTextIcon
                                                @click.stop="goToDetail(item._id)"
                                                class="text-info cursor-pointer mr-4"
                                                size="1.3x"
                                            ></FileTextIcon>
                                            <Trash2Icon
                                                @click.stop="deleteDiagnostic(item._id)"
                                                class="text-danger cursor-pointer"
                                                size="1.3x"
                                            ></Trash2Icon>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <Paginator
                    @change="onPageChange"
                    :current-page="currentPage"
                    :page-count="pageCount"
                    :total-items="totalItems"
                    :items-per-page="itemsPerPage"
                />
            </div>
            <EmptyList v-else :label="emptyLabel" />
        </template>
    </Layout>
</template>

<script>
import Layout from '@/components/Layout'
import Loader from '@/components/Loader'
import EmptyList from '@/components/EmptyList'
import Paginator from '@/components/Paginator'
import { ClockIcon, Trash2Icon, FileTextIcon } from 'vue-feather-icons'
import Url from '@/service/url'
import helpers from '@/service/helpers'

export default {
    name: 'diagnostic',

    components: {
        Layout,
        Loader,
        EmptyList,
        ClockIcon,
        Trash2Icon,
        Paginator,
        FileTextIcon
    },

    data() {
        return {
            isLoading: false,
            list: [],
            currentPage: 1,
            pageCount: 0,
            totalItems: 0,
            itemsPerPage: 25,
            emptyLabel: 'No live diagnostic data available.'
        }
    },

    watch: {
        $route() {
            const url = new Url()
            this.findMany(url.buildQuery())
        }
    },

    mounted() {
        this.findMany()
    },

    methods: {
        ...helpers,

        async findMany() {
            const url = new Url()
            const query = url.buildQuery()

            this.isLoading = true

            try {
                const response = await this.$store.dispatch('diagnostic/findDiagnostics', query)

                if (response.status === 200) {
                    const surveys = response.data.data

                    this.list = surveys.data
                    this.pageCount = surveys.page_count
                    this.totalItems = surveys.total
                    this.currentPage = parseFloat(surveys.current_page)
                    this.emptyLabel = 'No live diagnostic data available.'
                }
            } catch (error) {
                console.error('Error fetching live diagnostic data:', error)
            } finally {
                this.isLoading = false
            }
        },

        onPageChange(page) {
            var url = new Url()

            this.$router
                .push({
                    name: this.$router.currentRoute.name,
                    query: url.buildQuery({ page })
                })
                .catch(error => {
                    if (
                        error.name !== 'NavigationDuplicated' &&
                        !error.message.includes('Avoided redundant navigation to current location')
                    ) {
                        console.log(error)
                    }
                })
        },

        deleteDiagnostic(id) {
            this.$vueConfirm.confirm(
                {
                    message: 'Are you sure you want to delete this live diagnostic?',
                    button: {
                        no: 'Cancel',
                        yes: 'Delete'
                    }
                },
                confirm => {
                    if (confirm) {
                        this.confirmDelete(id)
                    }
                }
            )
        },

        async confirmDelete(id) {
            try {
                const response = await this.$store.dispatch('diagnostic/deleteDiagnostic', id)

                if (response.status === 200) {
                    this.findMany()
                    window.shout.success('Live Diagnostic deleted successfully')
                }
            } catch (error) {
                console.error('Error deleting live diagnostic:', error)
                window.shout.error('Failed to delete live diagnostic')
            }
        },

        goToDetail(id) {
            this.$router.push({ name: 'log.diagnostics-detail', params: { id } })
        }
    }
}
</script>

<style lang="scss" scoped>
.feedback-preview {
    display: inline-block;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.table {
    th {
        border-top: none;
    }
}

.theme-dark-mode {
    .table {
        td,
        th {
            border-color: rgba(255, 255, 255, 0.1);
        }
    }
}
</style>
